"use client";

import React, { useMemo, useState } from "react";
import NextLink from "next/link";
import { useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { useArtCatalogueEnabled } from "src/utils/art-catalogue-enabled";
import { useMapsEnabled } from "src/utils/maps-enabled";
import { useGiftCardsEnabled } from "src/utils/gift-cards-enabled";
import { useTranslations, useLocale } from "next-intl";
import {
  Box,
  Card,
  CardActionArea,
  Container,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ImageIcon from "@mui/icons-material/Image";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useBundleCards } from "src/hooks/useBundles";
import { useShopCurrency } from "src/components/cashback/use-client-value";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { Header } from "@components/header";
import { useProductWithMediaDefinitions } from "src/hooks/api/useProductWithMedia";
import { photosApi } from "src/services/api";
import { TEMPLATE_CATEGORIES } from "src/components/greeting-card/constants";
import { useResolvedTemplates } from "src/hooks/api/useTemplates";
import TemplateThumb from "src/components/greeting-card/editor/TemplateThumb";
import { THUMBNAIL_LOCALE } from "src/components/greeting-card/editor/template-preview-render";
import {
  pickerTemplatesForKind,
  sortTemplatesForKind,
} from "src/components/greeting-card/editor/template-filtering";
import { formatPwmPrice, resolvePwmPrice } from "src/components/greeting-card/pwm-price";
import { nextStepPrices } from "src/components/checkout/summary/summary-next-links";
import {
  buildProduktiTiles,
  LARGE_PRINT_PRICES,
  TEMPLATE_KINDS_WITH_PREVIEW,
  type ProduktiDefinition,
  type ProduktiHero,
  type ProduktiTile,
} from "./produkti-tiles";

/**
 * The shop's front door for products: every way to turn photos into
 * something, on one page, each with a picture, a line that says what it is,
 * the entry price where one is confirmed, and a visible verb.
 *
 * It used to be a heading-and-rule section per definition kind, which with
 * two definitions live meant two headings, two lone cards and two thirds of
 * the viewport empty — and no photo prints, calendars, bundles or gift cards,
 * all of which the header's Produkti menu offers. The tile list itself is
 * built in produkti-tiles.ts, which is where the ordering and the switches
 * are tested.
 */

// ---------------------------------------------------------------------------
// Heroes
// ---------------------------------------------------------------------------

/** Fills whatever frame the tile gives it; the tile owns the aspect ratio. */
const fillFrame = {
  width: "100%",
  height: "100%",
  display: "block",
} as const;

/**
 * No preview for this definition. A map has no template and, on stage, no
 * uploaded preview either, so it gets a map rather than the broken-image
 * glyph a customer reads as "something failed".
 */
const EmptyFrame: React.FC<{ kind?: string }> = ({ kind }) =>
  kind === "map" ? (
    <Box
      sx={(theme) => ({
        ...fillFrame,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: theme.tokens.brandSoft,
        color: theme.tokens.brand,
      })}
    >
      <MapOutlinedIcon className="tileArt" sx={{ fontSize: { xs: 44, sm: 64 } }} />
    </Box>
  ) : (
    <Box
      sx={{
        ...fillFrame,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "action.hover",
      }}
    >
      <ImageIcon sx={{ fontSize: 56, color: "action.disabled" }} />
    </Box>
  );

/**
 * The definition's own preview image, else `fallback`.
 *
 * The preview is resolved through GET /photos/download-url, which needs a
 * session, so an anonymous visitor (most of them) got a 401 and a grey
 * placeholder icon. A public URL from the definition is used directly when the
 * backend provides one; otherwise a failed lookup or a broken image steps
 * aside for `fallback` (a template preview for template-driven products).
 * Mirrors PwmCartThumb in src/components/cart-drawer/index.tsx.
 */
const DefinitionThumb: React.FC<{
  previewKey?: string;
  previewUrl?: string | null;
  fallback?: React.ReactNode;
  alt: string;
  kind?: string;
}> = ({ previewKey, previewUrl, fallback, alt, kind }) => {
  // A key stored in the database is not proof the object exists. Presigning
  // succeeds for a missing object, so the URL resolves and only the <img> tag
  // finds out — which is how a product whose preview file was never uploaded
  // rendered as a broken image with its alt text showing.
  const [broken, setBroken] = useState(false);
  const { data: lookedUp, isLoading: lookingUp } = useQuery({
    queryKey: ["produkti", "preview-url", previewKey],
    queryFn: async () => {
      const res = await photosApi.photoControllerGetDownloadUrl(previewKey as string);
      return (res.data as unknown as { url?: string } | undefined)?.url ?? null;
    },
    // 401 for anonymous visitors is the common case: no retry, and the
    // failure simply means "use the fallback".
    enabled: !previewUrl && !!previewKey,
    retry: false,
    staleTime: 5 * 60_000,
  });
  const url = previewUrl ?? lookedUp ?? null;
  const loading = !previewUrl && !!previewKey && lookingUp;

  if (loading) return <Skeleton variant="rectangular" sx={fillFrame} />;

  if (url && !broken) {
    return (
      <Box
        component="img"
        src={url}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
        className="tileArt"
        sx={(theme) => ({
          ...fillFrame,
          // `contain`, not `cover`: these previews are whole products, not
          // scenery. Cropping cut the text off a portrait invitation; the
          // point of the tile is to show what the thing looks like.
          objectFit: "contain",
          p: 1.5,
          boxSizing: "border-box",
          // A print's mat board — stays white in dark mode on purpose, see
          // src/themes/tokens.ts.
          bgcolor: theme.tokens.mat,
        })}
      />
    );
  }

  if (fallback) return <>{fallback}</>;
  return <EmptyFrame kind={kind} />;
};

/**
 * The product's best-matching template, drawn in the visitor's language
 * (TemplateThumb). Used when the product has no preview image of its own, or
 * the uploaded one is a Slovenian design and the visitor is not Slovenian.
 */
const TemplateHero: React.FC<{ kind: string; filter: string | null; alt: string }> = ({
  kind,
  filter,
  alt,
}) => {
  const locale = useLocale();
  const { data, isLoading } = useResolvedTemplates(kind, []);
  const template = useMemo(() => {
    const list = sortTemplatesForKind(
      pickerTemplatesForKind(
        kind,
        (data ?? []).map((t) => ({ ...t, id: t.code, categories: t.categories ?? [] })),
      ),
      kind,
    );
    return (filter && list.find((t) => t.categories.includes(filter))) || list[0] || null;
  }, [data, kind, filter]);

  if (isLoading) return <Skeleton variant="rectangular" sx={fillFrame} />;
  if (!template) return <EmptyFrame />;
  return (
    <Box sx={(theme) => ({ ...fillFrame, position: "relative", bgcolor: theme.tokens.mat })}>
      {/* Inset so a whole page shows with a little air around it (contain). */}
      <Box
        className="tileArt"
        sx={{
          position: "absolute",
          inset: 12,
          filter: "drop-shadow(0 2px 6px rgba(15,23,42,0.18))",
        }}
      >
        <TemplateThumb
          template={{ id: template.code, slots: template.slots, thumbnailUrl: template.thumbnailUrl ?? undefined }}
          locale={locale}
          alt={alt}
          fit="contain"
        />
      </Box>
    </Box>
  );
};

const IconHero: React.FC<{ icon: "bundle" | "gift" }> = ({ icon }) => {
  const Icon = icon === "bundle" ? Inventory2OutlinedIcon : CardGiftcardOutlinedIcon;
  return (
    <Box
      sx={(theme) => ({
        ...fillFrame,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        // A tint of the brand marks the two tiles that are not a printed
        // thing; never a whole-panel wash of it (tokens.ts).
        bgcolor: theme.tokens.brandSoft,
        color: theme.tokens.brand,
      })}
    >
      <Icon className="tileArt" sx={{ fontSize: { xs: 44, sm: 64 } }} />
    </Box>
  );
};

const TileHero: React.FC<{ hero: ProduktiHero; alt: string; filter: string | null }> = ({
  hero,
  alt,
  filter,
}) => {
  const locale = useLocale();
  switch (hero.kind) {
    case "image":
      return (
        <Box
          component="img"
          src={hero.src}
          alt=""
          loading="lazy"
          decoding="async"
          className="tileArt"
          sx={{ ...fillFrame, objectFit: "cover" }}
        />
      );
    case "icon":
      return <IconHero icon={hero.icon} />;
    case "skeleton":
      return <Skeleton variant="rectangular" sx={fillFrame} />;
    case "definition": {
      const def = hero.def;
      const kind = def.kind ?? "";
      const templateHero = TEMPLATE_KINDS_WITH_PREVIEW.has(kind) ? (
        <TemplateHero kind={kind} filter={filter} alt={alt} />
      ) : undefined;
      // The uploaded preview of a template product is a Slovenian design
      // (the Slovak shop showed "50 let"); other shops get a template drawn
      // in their own language instead.
      if (templateHero && locale !== THUMBNAIL_LOCALE) return templateHero;
      return (
        <DefinitionThumb
          previewKey={def.previewImageKey}
          previewUrl={def.previewImageUrl}
          fallback={templateHero}
          alt={alt}
          kind={kind}
        />
      );
    }
  }
};

// ---------------------------------------------------------------------------
// Tile
// ---------------------------------------------------------------------------

/**
 * One product, one click target.
 *
 * On a phone the tile lies down — picture on the left, words beside it — so
 * eight products fit in two screens instead of eight. From `sm` up it stands:
 * a 4:3 picture band over the text, four across on a wide screen.
 *
 * The bottom row answers the two questions that otherwise stop the click:
 * what it costs (where a figure is confirmed) and what happens when you
 * press (the verb, with an arrow that moves on hover).
 */
const Tile: React.FC<{ tile: ProduktiTile; filter: string | null }> = ({ tile, filter }) => {
  const t = useTranslations("produkti");
  const cta = t(tile.cta === "view" ? "ctaView" : "cta");
  const isSkeleton = tile.hero.kind === "skeleton";
  const body = (
    <>
      <Box
        sx={{
          flexShrink: 0,
          width: { xs: 128, sm: "100%" },
          aspectRatio: { xs: "1", sm: "4 / 3" },
          overflow: "hidden",
          bgcolor: "action.hover",
          "& .tileArt": { transition: "transform 400ms ease" },
        }}
      >
        <TileHero hero={tile.hero} alt={tile.title} filter={filter} />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          p: { xs: 1.5, sm: 2 },
          pt: { xs: 1.5, sm: 1.75 },
        }}
      >
        {isSkeleton ? (
          <>
            <Skeleton width="70%" height={26} />
            <Skeleton width="95%" height={18} sx={{ mt: 0.5 }} />
            <Skeleton width="40%" height={18} sx={{ mt: "auto" }} />
          </>
        ) : (
          <>
            <Typography
              variant="subtitle1"
              component="h2"
              sx={{ fontWeight: 700, lineHeight: 1.25 }}
            >
              {tile.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {tile.description}
            </Typography>
            <Box
              sx={{
                mt: "auto",
                pt: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0 }}>
                {tile.priceLabel ?? ""}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                className="tileCta"
                sx={(theme) => ({
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontWeight: 600,
                  color: theme.tokens.brand,
                  whiteSpace: "nowrap",
                  "& svg": { fontSize: 18, transition: "transform 200ms ease" },
                })}
              >
                {cta}
                <ArrowForwardRoundedIcon aria-hidden />
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </>
  );

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        height: "100%",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
        ...(isSkeleton
          ? {}
          : {
              "@media (hover: hover)": {
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: `0 12px 28px -12px ${alpha(theme.palette.common.black, 0.35)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.5),
                },
                "&:hover .tileArt": { transform: "scale(1.04)" },
                "&:hover .tileCta svg": { transform: "translateX(3px)" },
              },
            }),
      })}
    >
      {isSkeleton ? (
        <Box sx={{ height: "100%", display: "flex", flexDirection: { xs: "row", sm: "column" } }}>
          {body}
        </Box>
      ) : (
        <CardActionArea
          component={NextLink as React.ElementType}
          href={tile.href}
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: { xs: "row", sm: "column" },
            alignItems: "stretch",
            "&:focus-visible": {
              outline: "2px solid",
              outlineColor: "primary.main",
              outlineOffset: -2,
            },
          }}
        >
          {body}
        </CardActionArea>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ProduktiPage() {
  const artCatalogueEnabled = useArtCatalogueEnabled();
  const mapsEnabled = useMapsEnabled();
  const giftCardsEnabled = useGiftCardsEnabled();
  const t = useTranslations("produkti");
  const locale = useLocale();
  const translate = useAppTranslate();
  // A Paketi tile only while bundles are switched on and one is offered.
  const shopCurrency = useShopCurrency();
  const { data: bundleCards } = useBundleCards(shopCurrency, locale);
  const bundlesOffered = (bundleCards?.length ?? 0) > 0;

  // Optional template-category deep link: /produkti?filter=birthday (legacy
  // ?category= also accepted). Validated against the known slugs, then carried
  // onto every design link so the editor opens with that chip preselected.
  const searchParams = useSearchParams();
  const rawFilter = searchParams?.get("filter") ?? searchParams?.get("category");
  const filterParam =
    rawFilter && (TEMPLATE_CATEGORIES as readonly string[]).includes(rawFilter)
      ? rawFilter
      : null;

  const { data: definitionsPage, isLoading } = useProductWithMediaDefinitions();
  // The generated API types data as Array<object>; cast to our local interface.
  const definitions = (definitionsPage?.data ?? []) as ProduktiDefinition[];

  const localizedName = (def: ProduktiDefinition): string => {
    const name = def.name;
    return typeof name === "object" && name !== null
      ? (name[locale] ?? name.sl ?? name.en ?? Object.values(name)[0] ?? "")
      : String(name ?? "");
  };

  // A variant product carries no price of its own — the sizes do — so it
  // reports 0 and would advertise "from 0.00 €". Say nothing rather than
  // something false. Resolved the way the backend charges (a Polish visitor
  // sees złoty from priceCurrencies, not the euro base under a € sign), and
  // nothing until the currency is known on the client, rather than a flash
  // of €.
  const definitionPriceLabel = (def: ProduktiDefinition): string | undefined =>
    shopCurrency && def.price != null && def.price > 0
      ? t("fromPrice", {
          price: formatPwmPrice(
            resolvePwmPrice(def.price, def.priceCurrencies, shopCurrency),
            shopCurrency,
          ),
        })
      : undefined;

  // The calendar and canvas entry prices were read off the live catalogue
  // per market for the order-confirmation page (summary-next-links.ts);
  // markets without a confirmed figure show none.
  const confirmed = nextStepPrices(locale);
  const large = LARGE_PRINT_PRICES[(locale || "").toLowerCase().split("-")[0]];
  const staticPriceLabel = (key: "calendar" | "largePaper" | "largeCanvas"): string | undefined => {
    const price = key === "calendar" ? confirmed.calendar : large?.[key];
    return price ? t("fromPrice", { price }) : undefined;
  };

  const tiles = useMemo(
    () =>
      buildProduktiTiles({
        locale,
        definitions,
        definitionsLoading: isLoading,
        flags: { artCatalogueEnabled, mapsEnabled, bundlesOffered, giftCardsEnabled },
        translate,
        localizedName,
        definitionPriceLabel,
        staticPriceLabel,
        filterParam,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale, definitions, isLoading, artCatalogueEnabled, mapsEnabled, bundlesOffered, giftCardsEnabled, shopCurrency, filterParam],
  );

  return (
    <>
      <Header sticky={false} />

      {/* Same shape as the wall-art hub: kicker, a title that says what the
          page is for, one line of lede. Left-aligned with the grid, not
          centred over it. */}
      <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 3, md: 4 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="overline"
            component="p"
            color="text.secondary"
            sx={{ letterSpacing: ".14em", lineHeight: 1.6 }}
          >
            {t("title")}
          </Typography>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, mt: 0.5, mb: 1.5, maxWidth: "22ch", fontSize: { xs: "1.9rem", md: "3rem" } }}
          >
            {t("heroTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: "58ch" }}>
            {t("lede")}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: { xs: 6, md: 10 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 2.5, md: 3 }} component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
          {tiles.map((tile) => (
            <Grid
              key={tile.key}
              size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
              component="li"
              aria-busy={tile.hero.kind === "skeleton" || undefined}
            >
              <Tile tile={tile} filter={filterParam} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </>
  );
}
